import { useMemo, useEffect } from 'react'
import { Vertical, Horizontal, Texto, MaterialIcon, GraviButton, BBDTag } from '@gravitate-js/excalibrr'
import { Card, Tooltip, Popover, Select, Avatar, Popconfirm } from 'antd'
import type { MatchDetailResponse } from '../../../../../../api/useSupplierReconciliation/useMatchDetail'
import type { components } from '../../../../../../types/api/v1'
import { useMatchDetailState } from '../../hooks/useMatchDetailState'
import { MATCH_STATUS_CONFIG } from '../../../constants/statusConfig'
import type { SupplierReconciliationMetadataResponse } from '../../../../../../api/useMetadata/types'
import { useReasonCodes } from '../../../hooks/useReasonCodes'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { OnHoldInfoPopover } from '../../../components/OnHoldInfoPopover'
import { InvoiceCard } from './InvoiceCard'
import { BOLCard } from './BOLCard'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useMatchActions } from '../../../hooks'
import { getCanUnmatch, getDocumentsToUnmatch } from './util'

export type MatchDetailRow = components['schemas']['MatchDetailRow']

export interface MatchDetailPaneProps {
  match: MatchDetailResponse
  matchActions: {
    handleIndividualHold: (matchId: string, reasonCode?: string) => Promise<void>
    handleIndividualUnhold: (matchId: string) => Promise<void>
    handleIndividualUnapprove: (matchId: string) => Promise<void>
    handleIndividualApprove: (matchId: string, reasonCode: string) => Promise<void>
    handleVoidInvoice: (invoiceId: string) => Promise<void>
    handleUnvoidInvoice: (invoiceId: string) => Promise<void>
    approveMatchMutation: { mutate: (data: { match_id: string; reason_code: string }) => void; isLoading: boolean }
    waitingOnSupplierMutation: {
      mutate: (data: { match_id: string; reason_code?: string }) => void
      isLoading: boolean
    }
    voidInvoiceMutation: { mutate: (invoiceId: string) => void; isLoading: boolean }
    unvoidInvoiceMutation: { mutate: (invoiceId: string) => void; isLoading: boolean }
  }
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
  modelConfig?: {
    approve_reason_required?: boolean
    hold_reason_required?: boolean
  }
}

/**
 * Left sidebar component showing match details using existing components
 */
export function MatchDetailPane({ match, matchActions, metadata, metadataLoading, modelConfig }: MatchDetailPaneProps) {
  const { scopes } = useSecurityContext()

  // Check if user has bulk approve permission
  const hasBulkApprovePermission = scopes?.supplier_rec?.bulk_approve || false

  const {
    selectedReasonCode,
    setSelectedReasonCode,
    approvePopoverOpen,
    setApprovePopoverOpen,
    holdPopoverOpen,
    setHoldPopoverOpen,
    selectedHoldReason,
    setSelectedHoldReason,
  } = useMatchDetailState(match)

  const statusConfig = MATCH_STATUS_CONFIG[match.status]
  const isMatchApproved = match?.status === 'approved'
  const isMatchUnmatched = match?.status === 'unmatched'
  const isOnHold = match?.status === 'hold'
  const isUnapproved = match?.status === 'unapproved'
  const isExported = match?.export_status === 'sent'
  const canUnapprove = isMatchApproved && hasBulkApprovePermission

  // Group documents by document_id and aggregate totals properly
  const documentGroups =
    match?.document_detail_rows?.reduce(
      (acc: Record<string, MatchDetailRow & { rows: MatchDetailRow[] }>, row: MatchDetailRow) => {
        const key = row.document_id
        if (!acc[key]) {
          // First row for this document - use it as the base
          acc[key] = {
            ...row,
            rows: [row],
          }
        } else {
          // Additional rows for this document - just add to the total
          acc[key].total += row.total || 0
          acc[key].rows.push(row)
        }
        return acc
      },
      {}
    ) || {}

  // Convert to array and get unique documents with aggregated totals
  const uniqueDocuments = Object.values(documentGroups)

  const invoiceDocuments = uniqueDocuments.filter((row) => row.document_type === 'invoice')
  const bolDocuments = uniqueDocuments.filter((row) => row.document_type === 'bol')

  // Only show invoices that exist in document_detail_rows (no pseudo-invoices from activity log)
  const allInvoiceDocuments = invoiceDocuments
  const hasInvoice = allInvoiceDocuments.length > 0
  const hasBol = bolDocuments.length > 0

  // Check if delivery reconciliation should block approval
  const isBlockedByDeliveryRec = useMemo(() => {
    const deliveryReconciliationMode = modelConfig?.supplier_reconciliation?.delivery_reconciliation_mode

    if (deliveryReconciliationMode !== 'block_approval') return false
    return match?.warnings?.some((w) => w.type === 'delivery_reconciliation') || false
  }, [modelConfig, match?.warnings])

  // Check if delivery reconciliation errors exist (regardless of blocking mode)
  const hasDeliveryRecError = useMemo(() => {
    return match?.warnings?.some((w) => w.type === 'delivery_reconciliation') || false
  }, [match?.warnings])

  const canApprove =
    hasInvoice && hasBol && !isMatchUnmatched && !isMatchApproved && hasBulkApprovePermission && !isBlockedByDeliveryRec

  // Get separated reason codes using the new hook
  const { approvalReasonCodeOptions, holdReasonCodeOptions } = useReasonCodes(metadata)

  // Set default reason code when options are loaded
  useEffect(() => {
    if (approvalReasonCodeOptions.length > 0 && !selectedReasonCode) {
      setSelectedReasonCode(approvalReasonCodeOptions[0].value)
    }
  }, [approvalReasonCodeOptions, selectedReasonCode, setSelectedReasonCode])

  const handleApproveWithReason = async () => {
    if (match?.id && selectedReasonCode) {
      await matchActions.handleIndividualApprove(match.id, selectedReasonCode)
      setApprovePopoverOpen(false)
      setSelectedReasonCode('')
    }
  }

  const handleHoldWithReason = async () => {
    if (match?.id) {
      await matchActions.handleIndividualHold(match.id, selectedHoldReason?.label?.toString() || undefined)
      setHoldPopoverOpen(false)
      setSelectedHoldReason(null)
    }
  }

  const handleVoidInvoiceWithNavigation = async (invoiceId: string) => {
    await matchActions.handleVoidInvoice(invoiceId)
  }

  const { handleMerge } = useMatchActions()
  const canUnmatch = getCanUnmatch(match, [...allInvoiceDocuments, ...bolDocuments])
  function handleUnmatch(item: MatchDetailRow) {
    const { invoices, bols } = getDocumentsToUnmatch(item, allInvoiceDocuments, bolDocuments)
    handleMerge(match.id, { invoices, bols }, true)
  }

  return (
    <div
      style={{
        background: 'var(--theme-bg-default)',
        height: '100%',
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr',
        gap: '16px',
      }}
    >
      {/* Main Card with Match Details */}
      <Vertical gap={12} border='all'>
        {/* Status and Action Buttons Row */}
        <Vertical className='p-3' gap={8} style={{ backgroundColor: 'var(--bg-2)' }}>
          <Horizontal justifyContent='space-between' verticalCenter>
            <Horizontal gap='6px' verticalCenter>
              <Texto category='h5' weight='bold'>
                Match
              </Texto>
              {match.status === 'hold' ? (
                <Popover content={<OnHoldInfoPopover data={match} />} title={null} trigger='hover' placement='top'>
                  <BBDTag
                    error={statusConfig?.tagProps?.error}
                    warning={statusConfig?.tagProps?.warning}
                    success={statusConfig?.tagProps?.success}
                    theme2={statusConfig?.tagProps?.theme2}
                    icon={<MaterialIcon icon={statusConfig?.icon || 'circle'} fill size={8} />}
                    style={{ fontSize: '12px', cursor: 'pointer' }}
                  >
                    <span className='pl-1'>{statusConfig?.display || match.status}</span>
                  </BBDTag>
                </Popover>
              ) : (
                <BBDTag
                  error={statusConfig?.tagProps?.error}
                  warning={statusConfig?.tagProps?.warning}
                  success={statusConfig?.tagProps?.success}
                  theme2={statusConfig?.tagProps?.theme2}
                  icon={<MaterialIcon icon={statusConfig?.icon || 'circle'} fill size={8} />}
                  style={{ fontSize: '12px' }}
                >
                  <span className='pl-1'>{statusConfig?.display || match.status}</span>
                </BBDTag>
              )}
            </Horizontal>
            <Texto>Actions</Texto>
          </Horizontal>

          {/* Match Info */}
          <Horizontal justifyContent='space-between' verticalCenter>
            <Horizontal verticalCenter gap={8}>
              <Avatar
                size='small'
                style={{
                  background: match.assignee?.name ? 'var(--theme-color-1)' : 'var(--gray-100)',
                  color: match.assignee?.name ? 'white' : 'var(--gray-800)',
                }}
              >
                {match.assignee?.name ? match.assignee.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Texto category='p2' appearance='light'>
                {match.assignee?.name || 'Unassigned'}
              </Texto>
            </Horizontal>
            {/* Action Buttons */}
            <Horizontal gap={8}>
              {/* Hold Button - Hide for approved matches */}
              {!isMatchApproved &&
                (isOnHold ? (
                  <Tooltip title='Remove Hold'>
                    <GraviButton
                      onClick={async () => {
                        if (match?.id) {
                          await matchActions.handleIndividualUnhold(match.id)
                        }
                      }}
                      loading={matchActions.waitingOnSupplierMutation.isPending}
                      appearance='outline'
                      theme1
                      size='small'
                      icon={<MaterialIcon icon='close' style={{ fontSize: 16 }} className='pt-1' />}
                    />
                  </Tooltip>
                ) : (
                  <Popover
                    content={
                      <Vertical gap={12} style={{ width: 220 }}>
                        <Vertical gap={8}>
                          <Texto category='p2' weight='600'>
                            Mark On Hold
                          </Texto>
                          <Texto category='p3' appearance='hint'>
                            Temporarily pause processing while awaiting supplier clarification.
                          </Texto>
                          <Texto category='p3' weight='600'>
                            Select Hold Reason (optional):
                          </Texto>
                          <Select
                            style={{ width: '100%' }}
                            placeholder='Choose a reason for hold'
                            value={selectedHoldReason || undefined}
                            onChange={(value) => setSelectedHoldReason(value)}
                            options={holdReasonCodeOptions}
                            loading={metadataLoading || holdReasonCodeOptions.length === 0}
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                              String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            disabled={holdReasonCodeOptions.length === 0 && !metadataLoading}
                            labelInValue
                          />
                        </Vertical>

                        <Horizontal gap={8} justifyContent='flex-end' style={{ marginTop: 8 }}>
                          <GraviButton
                            appearance='outlined'
                            buttonText='Cancel'
                            onClick={() => {
                              setHoldPopoverOpen(false)
                              setSelectedHoldReason(null)
                            }}
                          />
                          <GraviButton
                            theme1
                            buttonText='Mark On Hold'
                            loading={matchActions.waitingOnSupplierMutation.isPending}
                            onClick={handleHoldWithReason}
                          />
                        </Horizontal>
                      </Vertical>
                    }
                    title={null}
                    trigger='click'
                    open={holdPopoverOpen}
                    onOpenChange={setHoldPopoverOpen}
                    placement='bottomRight'
                  >
                    <Tooltip title='Mark On Hold'>
                      <GraviButton
                        appearance='outline'
                        size='small'
                        icon={<MaterialIcon icon='pause' style={{ fontSize: 16 }} className='pt-1' />}
                      />
                    </Tooltip>
                  </Popover>
                ))}

              {/* Approve Button - Only show when status is 'matched' (unapproved) */}
              {isUnapproved && (
                <Popover
                  content={
                    <Vertical gap={12} style={{ width: 300 }}>
                      <Vertical gap={8}>
                        <Texto category='p2' weight='600'>
                          Select Approval Reason:
                        </Texto>
                        <Select
                          style={{ width: '100%' }}
                          placeholder='Choose a reason for approval'
                          value={selectedReasonCode || undefined}
                          onChange={(value) => setSelectedReasonCode(value)}
                          options={approvalReasonCodeOptions}
                          loading={metadataLoading || approvalReasonCodeOptions.length === 0}
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.label ?? '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          disabled={approvalReasonCodeOptions.length === 0 && !metadataLoading}
                        />
                      </Vertical>

                      {(() => {
                        // Calculate filtered warnings for variance details
                        const varianceWarnings =
                          match?.warnings?.filter(
                            (w) =>
                              w.type !== 'movement_validation' &&
                              w.type !== 'delivery_reconciliation' &&
                              w.threshold !== undefined &&
                              w.threshold !== null &&
                              w.threshold !== 0
                          ) || []

                        // Calculate delivery reconciliation warnings
                        const deliveryRecWarnings =
                          match?.warnings?.filter((w) => w.type === 'delivery_reconciliation') || []

                        return (
                          <>
                            {/* Display variance details only when variance warnings exist */}
                            {varianceWarnings.length > 0 && (
                              <Vertical
                                gap={8}
                                style={{
                                  padding: 12,
                                  background: 'var(--theme-warning-dim)',
                                  borderRadius: 4,
                                  border: '1px solid var(--theme-warning)',
                                }}
                              >
                                <Texto category='p2' weight='600' style={{ color: 'var(--theme-warning)' }}>
                                  Variance Details:
                                </Texto>
                                {varianceWarnings.map((warning, index) => (
                                  <Vertical
                                    key={warning.id || `warning-${index}`}
                                    gap={4}
                                    style={{
                                      paddingTop: index > 0 ? 8 : 0,
                                      borderTop: index > 0 ? '1px solid var(--theme-separator)' : 'none',
                                    }}
                                  >
                                    <Texto category='p3' weight='500'>
                                      {warning.type
                                        .replace(/^by_/, '')
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                                    </Texto>
                                    <Texto category='p3' appearance='hint'>
                                      {warning.message}
                                    </Texto>
                                    <Horizontal gap={16}>
                                      <Texto category='p3'>
                                        <span style={{ color: 'var(--theme-text-secondary)' }}>Variance:</span>{' '}
                                        <span style={{ fontWeight: 600, color: 'var(--theme-warning)' }}>
                                          {warning.variance?.toFixed(2) || '0'}
                                          {warning.unit}
                                        </span>
                                      </Texto>
                                      <Texto category='p3'>
                                        <span style={{ color: 'var(--theme-text-secondary)' }}>Threshold:</span>{' '}
                                        <span style={{ fontWeight: 600 }}>
                                          {warning.threshold?.toFixed(2) || '0'}
                                          {warning.unit}
                                        </span>
                                      </Texto>
                                    </Horizontal>
                                  </Vertical>
                                ))}
                              </Vertical>
                            )}

                            {/* Display delivery reconciliation warnings */}
                            {deliveryRecWarnings.length > 0 && (
                              <Vertical
                                gap={8}
                                style={{
                                  padding: 12,
                                  background: 'var(--theme-warning-dim)',
                                  borderRadius: 4,
                                  border: '1px solid var(--theme-warning)',
                                }}
                              >
                                <Horizontal gap={8} verticalCenter>
                                  <MaterialIcon
                                    icon='local_shipping'
                                    style={{ color: 'var(--theme-warning)' }}
                                    size={16}
                                  />
                                  <Texto category='p2' weight='600' style={{ color: 'var(--theme-warning)' }}>
                                    Delivery Reconciliation
                                  </Texto>
                                </Horizontal>
                                <Texto category='p3' appearance='hint'>
                                  Delivery reconciliation errors present
                                </Texto>
                              </Vertical>
                            )}
                          </>
                        )
                      })()}

                      <Horizontal gap={8} justifyContent='flex-end' style={{ marginTop: 8 }}>
                        <GraviButton
                          appearance='outlined'
                          buttonText='Cancel'
                          onClick={() => {
                            setApprovePopoverOpen(false)
                            setSelectedReasonCode('')
                          }}
                        />
                        <GraviButton
                          theme2
                          buttonText='Approve'
                          disabled={!selectedReasonCode}
                          loading={matchActions.approveMatchMutation.isPending}
                          onClick={handleApproveWithReason}
                        />
                      </Horizontal>
                    </Vertical>
                  }
                  title={null}
                  trigger='click'
                  open={approvePopoverOpen}
                  onOpenChange={setApprovePopoverOpen}
                  placement='bottomRight'
                >
                  <Tooltip
                    title={
                      !canApprove
                        ? !hasBulkApprovePermission
                          ? 'You do not have permission to approve'
                          : !hasInvoice
                            ? 'Match must have at least one invoice'
                            : !hasBol
                              ? 'Match must have at least one BOL'
                              : isBlockedByDeliveryRec
                                ? 'Approval Blocked: Delivery Reconciliation errors must be resolved'
                                : 'Match is already approved'
                        : hasDeliveryRecError &&
                            modelConfig?.supplier_reconciliation?.delivery_reconciliation_mode !== 'block_approval'
                          ? 'Approve Match: Delivery Reconciliation errors present but blocking is disabled'
                          : 'Approve Match'
                    }
                  >
                    <GraviButton
                      theme2
                      size='small'
                      disabled={!canApprove}
                      icon={<MaterialIcon icon='check_circle' />}
                    />
                  </Tooltip>
                </Popover>
              )}

              {canUnapprove && (
                <Tooltip title={isExported ? "Can't unapprove exported match" : 'Unapprove Match'}>
                  <span>
                    <Popconfirm
                      title='Unapprove Match'
                      description={`Are you sure you want to unapprove this match?`}
                      icon={<ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />}
                      onConfirm={() => {
                        if (match?.id) matchActions.handleIndividualUnapprove(match.id)
                      }}
                      okButtonProps={{ danger: true }}
                      okText='Confirm'
                      cancelText='Cancel'
                    >
                      <GraviButton
                        appearance='outlined'
                        size='small'
                        icon={<MaterialIcon style={{ paddingTop: '2px' }} icon='rotate_left' size={16} />}
                        disabled={isExported}
                      />
                    </Popconfirm>
                  </span>
                </Tooltip>
              )}
            </Horizontal>
          </Horizontal>
        </Vertical>
      </Vertical>

      {/* PO Numbers Card */}
      {(match as any)?.po_numbers && (match as any).po_numbers.length > 0 && (
        <Card className='ml-2' size='small' style={{ borderRadius: 8 }} styles={{ body: { padding: 12 } }}>
          <Vertical gap={4}>
            <Texto category='p2' appearance='hint'>
              PO Number{(match as any).po_numbers.length > 1 ? 's' : ''}
            </Texto>
            <Texto category='p2' weight='500'>
              {(match as any).po_numbers.join(', ')}
            </Texto>
          </Vertical>
        </Card>
      )}

      {/* Document Cards - Scrollable Container */}
      <div
        className='pb-2 pr-2'
        style={{
          overflow: 'auto',
        }}
      >
        <Vertical className='pl-3' height='auto' style={{ overflow: 'initial' }}>
          <Texto className='pb-1' category='p2' weight='600' style={{ marginBottom: '8px' }}>
            Invoices ({allInvoiceDocuments.length})
          </Texto>
          {(allInvoiceDocuments.length > 0 || match?.void_invoices?.length > 0) && (
            <Vertical gap={8} style={{ flexShrink: 0 }}>
              {[...allInvoiceDocuments, ...match.void_invoices].map((invoice) => (
                <InvoiceCard
                  key={invoice.document_id?.toString() ?? invoice.id?.toString()}
                  invoice={invoice as MatchDetailRow}
                  match={match}
                  hasBulkApprovePermission={hasBulkApprovePermission}
                  handleVoid={handleVoidInvoiceWithNavigation}
                  matchActions={matchActions}
                  handleUnmatch={canUnmatch ? handleUnmatch : null}
                />
              ))}
            </Vertical>
          )}

          {/* BOL Cards Section */}
          {bolDocuments.length > 0 && (
            <Vertical>
              <Texto className='pt-3 pb-1' category='p2' weight='600'>
                BOLs ({bolDocuments.length})
              </Texto>
              <Vertical height='auto' gap={8} style={{ flexShrink: 0 }}>
                {bolDocuments.map((bol: MatchDetailRow) => (
                  <BOLCard key={bol.document_id} bol={bol} handleUnmatch={canUnmatch ? handleUnmatch : null} />
                ))}
              </Vertical>
            </Vertical>
          )}
        </Vertical>
      </div>
    </div>
  )
}
